/**
 * Pre-take coaching banner — Pro Tools-style with a segmented LCD-LED meter.
 *
 * Shown above the BigRecordButton, anchored to the upcoming cue's predicted
 * dynamic category. Uses a 5-segment LED meter (PT-style green→yellow→red
 * intensity ramp) to communicate the dB tier at a glance.
 */
import type { CueCategory } from '@ohear/shared'
import { copy } from '@ohear/shared'

export interface PredictiveHintProps {
  category: CueCategory | null
  /** Locale-driven copy. Default 'en'. */
  locale?: 'zh' | 'en'
}

interface Tier {
  /** LED color (and label tint). */
  led: string
  /** Tier number 1–5 — how many LED segments to light. */
  level: number
  /** dB tag. */
  db: string
}

// PT-style segmented LED color ramp:
//   1 = green, 2 = green, 3 = yellow, 4 = orange, 5 = red.
const TIER: Record<CueCategory, Tier> = {
  whisper: { led: '#16ff37', level: 1, db: '+6 dB' },
  normal: { led: '#16ff37', level: 2, db: '0 dB' },
  loud: { led: '#ffe74a', level: 3, db: '−3 dB' },
  shout: { led: '#ffaa1d', level: 4, db: '−6 dB' },
  extreme: { led: '#ff3838', level: 5, db: '−3 dB · split' },
}

export function PredictiveHint({ category, locale = 'en' }: PredictiveHintProps) {
  if (!category) return null
  // Defensive guard: legacy/unknown predicted_category values (e.g. older
  // 'conversational', 'effort', 'breath' enum members from earlier schema)
  // would otherwise dereference undefined entries → TypeError → ErrorBoundary
  // and the whole record page goes dark. Bail out instead of crashing.
  const entry = copy.predictive.PREDICTIVE[category]
  const tier = TIER[category]
  if (!entry || !tier) return null

  return (
    <div
      role="status"
      aria-live="polite"
      className="flex items-center gap-3 rounded-sm border border-pt-border bg-pt-panel2 px-3 py-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.6),0_1px_0_rgba(0,0,0,0.18)]"
    >
      {/* Icon disc — small PT-style chrome bezel. */}
      <div
        aria-hidden="true"
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-pt-border bg-gradient-to-b from-pt-panel2 to-pt-panel text-base shadow-[inset_0_1px_0_rgba(255,255,255,0.7),0_1px_2px_rgba(0,0,0,0.18)]"
      >
        {entry.icon}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <span className="text-[13px] font-semibold text-pt-text">
            {locale === 'zh' ? entry.title.zh : entry.title.en}
          </span>
          <span className="pt-mono rounded-sm bg-pt-lcd-bg px-1.5 py-px text-[10px] uppercase text-pt-lcd-amber shadow-[inset_0_1px_2px_rgba(0,0,0,0.95)]">
            {tier.db}
          </span>
        </div>
        <div className="mt-0.5 text-[12px] leading-snug text-pt-text-soft">
          {locale === 'zh' ? entry.zh : entry.en}
        </div>
      </div>

      {/* PT-style segmented LED meter — black bezel, 5 colored pills. */}
      <div className="pt-led-meter shrink-0" aria-label={`Dynamic level: ${category}`}>
        {[1, 2, 3, 4, 5].map((i) => {
          const on = i <= tier.level
          // Color the lit segments per their position (green→yellow→red ramp).
          const ledColor =
            i <= 2 ? '#16ff37' : i === 3 ? '#ffe74a' : i === 4 ? '#ffaa1d' : '#ff3838'
          return (
            <span
              key={i}
              className={`pt-led-seg${on ? ' pt-led-seg--on' : ''}`}
              style={on ? { color: ledColor } : undefined}
            />
          )
        })}
      </div>
    </div>
  )
}
